import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { updateOpenGraphTags } from '../utils/opengraph';

const B2BLanding = ({ navigateToHome, navigateToBlog, navigateToB2B, backendUrl, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    clinicName: '',
    website: '',
    services: [],
    message: '',
    privacyAccepted: false, // GDPR checkbox
    honeypot: '', // Spam protection - hidden field
    submitTime: null // Spam protection - form fill time tracking
  });

  // Track form start time for spam protection
  const [formStartTime] = useState(Date.now());
  
  // Testimonials state
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);

  // Public settings state for visitor statistics
  const [publicSettings, setPublicSettings] = useState({
    visitor_stats_image: null,
    monthly_visitors: null
  });

  // Load public settings on component mount
  useEffect(() => {
    const loadPublicSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/settings/public`);
        if (response.ok) {
          const data = await response.json();
          setPublicSettings({
            visitor_stats_image: data.visitor_stats_image,
            monthly_visitors: data.monthly_visitors
          });
        }
      } catch (error) {
        console.error('Hiba a publikus beállítások betöltése során:', error);
      }
    };

    loadPublicSettings();
  }, []);

  // Load testimonials on component mount
  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/testimonials/public`);
        if (response.ok) {
          const data = await response.json();
          setTestimonials(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Hiba a vélemények betöltése során:', error);
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    loadTestimonials();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Check privacy policy acceptance
    if (!contactForm.privacyAccepted) {
      alert('Kérjük fogadja el az adatkezelési tájékoztatót!');
      return;
    }
    
    // Spam protection checks
    const timeTaken = Date.now() - formStartTime;
    
    // Check if honeypot field was filled (indicates bot)
    if (contactForm.honeypot) {
      console.log('Spam detected: honeypot field filled');
      return;
    }
    
    // Check if form was submitted too quickly (indicates bot)
    if (timeTaken < 3000) { // Less than 3 seconds
      alert('Kérjük töltse ki a formot körültekintően.');
      return;
    }
    
    try {
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          clinic_name: contactForm.clinicName,
          website: contactForm.website,
          services: contactForm.services,
          message: contactForm.message
        })
      });
      
      if (response.ok) {
        alert('Köszönjük megkeresését! Munkatársunk hamarosan felveszi Önnel a kapcsolatot.');
        setContactForm({
          name: '',
          email: '',
          phone: '',
          clinicName: '',
          website: '',
          services: [],
          message: '',
          privacyAccepted: false,
          honeypot: '',
          submitTime: null
        });
      } else {
        alert('Hiba történt az üzenet küldése során. Kérjük próbálja újra.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Hiba történt az üzenet küldése során. Kérjük próbálja újra.');
    }
  };

  const handleServiceChange = (service, checked) => {
    setContactForm(prev => ({
      ...prev,
      services: checked 
        ? [...prev.services, service]
        : prev.services.filter(s => s !== service)
    }));
  };

  const scrollToContact = () => {
    document.getElementById('contact-form').scrollIntoView({ behavior: 'smooth' });
  };

  // SEO setup for B2B page
  useEffect(() => {
    // Update page title
    document.title = "FogPortál.hu – Teljes online jelenlét fogorvosoknak és rendelőknek";
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.getElementsByTagName('head')[0].appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Fogorvosoknak és fogászatoknak szóló lehetőség: egyedi weboldal, országos jelenlét és célzott páciens elérés a FogPortál.hu segítségével');
    
    // Add keywords meta tag
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.getElementsByTagName('head')[0].appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'fogászat kereső, fogorvos kereső, fogászati rendelők, fogorvos Budapest, fogászati marketing, rendelő megjelenés, fogászati portál');
    
    // Add structured data for LocalBusiness
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "FogPortál.hu - Fogászati Marketing Szolgáltatások",
      "description": "Professzionális fogászati marketing megoldások és online megjelenés Magyarország prémium fogászati portálján",
      "provider": {
        "@type": "Organization",
        "name": "FogPortál.hu",
        "url": "https://fogportal.hu",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+36-1-234-5678",
          "contactType": "customer service",
          "areaServed": "HU",
          "availableLanguage": "Hungarian"
        }
      },
      "serviceType": "Fogászati Marketing",
      "areaServed": {
        "@type": "Country",
        "name": "Hungary"
      },
      "audience": {
        "@type": "BusinessAudience",
        "audienceType": "Fogászati rendelők és fogorvosok"
      }
    };
    
    // Remove existing structured data script if any
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Add new structured data
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.getElementsByTagName('head')[0].appendChild(script);
    
    // Update OpenGraph meta tags for B2B page
    updateOpenGraphTags({
      title: 'FogPortál.hu – Teljes online jelenlét fogorvosoknak és rendelőknek',
      description: 'Fogorvosoknak és fogászatoknak szóló lehetőség: egyedi weboldal, országos jelenlét és célzott páciens elérés a FogPortál.hu segítségével',
      type: 'website',
      url: `${window.location.origin}/megjelenes-fogaszatoknak`,
      imageAlt: 'FogPortál.hu - Fogászatok számára'
    }, publicSettings.default_og_image);
    
    // Cleanup function to reset meta tags when component unmounts
    return () => {
      document.title = "FogPortál.hu - Magyarország prémium fogászati portálja";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Találja meg a legjobb fogorvost Magyarországon! Több mint 1000 fogászati klinika egy helyen.');
      }
    };
  }, [publicSettings.default_og_image]);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Megjelenés fogászatoknak – FogPortál.hu"
        description="Legyen rendelője ott, ahol a páciensei keresik! Csatlakozzon a FogPortál.hu-hoz és érjen el több ezer potenciális pácienst."
        keywords="fogászat megjelenés, fogorvos marketing, fogászati portál, páciens szerzés"
        canonicalUrl={`${window.location.origin}/megjelenes-fogaszatoknak`}
      />
      {/* Modern Header */}
      <header className="bg-white shadow-lg border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center">
              <button 
                onClick={navigateToHome}
                className="flex items-center space-x-3"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 2.21 1.79 4 4 4s4-1.79 4-4v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2 15c0 1.1-.9 2-2 2s-2-.9-2-2v-1h4v1zm1.46-4.86C14.8 12.64 14 13.26 14 14v1h-4v-1c0-.74-.8-1.36-1.46-.86C7.06 12.5 6 10.84 6 9c0-3.31 2.69-6 6-6s6 2.69 6 6c0 1.84-1.06 3.5-2.54 4.14z"/>
                  </svg>
                </div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                  FogPortál.hu
                </h1>
              </button>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              <button 
                onClick={navigateToHome}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Főoldal
              </button>
              {/* TEMPORARILY HIDDEN - Blog Menu Item */}
              {/* <button 
                onClick={navigateToBlog}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Blog
              </button> */}
              <button 
                onClick={navigateToB2B}
                className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium text-sm hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Megjelenés fogászatoknak
              </button>
              <button 
                onClick={scrollToContact}
                className="px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-medium text-sm hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
              >
                Kapcsolat
              </button>
            </nav>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                  />
                </svg>
              </button>
            </div>
          </div>
          
          {/* Mobile menu dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
              <div className="flex flex-col space-y-2 pt-4">
                <button 
                  onClick={() => {
                    navigateToHome();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
                >
                  Főoldal
                </button>
                <button 
                  onClick={() => {
                    navigateToBlog();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
                >
                  Blog
                </button>
                <button 
                  onClick={() => {
                    navigateToB2B();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-3 text-left text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100 transition-all duration-200 shadow-sm"
                >
                  Megjelenés fogászatoknak
                </button>
                <button 
                  onClick={() => {
                    scrollToContact();
                    setIsMobileMenuOpen(false);
                  }}
                  className="px-4 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-left"
                >
                  Kapcsolat
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-green-50 py-20 lg:py-32">
        <div className="absolute inset-0 bg-white bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              Legyen rendelője ott, ahol a 
              <span className="text-blue-600"> páciensei keresik!</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
              A FogPortál.hu Magyarország prémium fogászati portálja, amely összeköti a pácienseket 
              a legjobb rendelőkkel. Csatlakozzon hozzánk és érjen el több ezer potenciális pácienst!
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-12 py-4 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-bold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Vegye fel velünk a kapcsolatot
            </button>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-20 h-20 bg-blue-200 rounded-full opacity-20"></div>
        <div className="absolute bottom-20 left-10 w-32 h-32 bg-green-200 rounded-full opacity-20"></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-blue-300 rounded-full opacity-10"></div>
      </section>

      {/* Why Join Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Miért éri meg csatlakozni a FogPortálhoz?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Több mint egy egyszerű listázás – teljes értékű online jelenlét és célzott páciens elérés.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-blue-50 hover:bg-blue-100 transition-colors">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Havi tízezres elérés</h3>
              <p className="text-gray-600">
                A portál látogatottsága havonta több tízezres nagyságrendben mozog, 
                így Ön hirdetési költség nélkül érhet el új pácienseket.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-green-50 hover:bg-green-100 transition-colors">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Saját weboldal</h3>
              <p className="text-gray-600">
                Prémium csomaggal egyedi, professzionális aloldalt is kap teljes funkcionalitással, 
                ami önálló weboldalként működik.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-purple-50 hover:bg-purple-100 transition-colors">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Célzott láthatóság</h3>
              <p className="text-gray-600">
                A páciensek város és szolgáltatás szerint kereshetnek, 
                így mindig a megfelelő célcsoport találja meg Önt.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-indigo-50 hover:bg-indigo-100 transition-colors">
              <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">SEO előnyök</h3>
              <p className="text-gray-600">
                Minden aloldal és a teljes portál keresőoptimalizált, 
                így könnyebben érhető el a Google-ből is.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-orange-50 hover:bg-orange-100 transition-colors">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Kiemelt lehetőségek</h3>
              <p className="text-gray-600">
                Extra láthatóság a főoldalon és PR megjelenések a blogban 
                további páciens elérésért.
              </p>
            </div>

            <div className="text-center p-8 rounded-2xl bg-teal-50 hover:bg-teal-100 transition-colors">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Folyamatos jelenlét</h3>
              <p className="text-gray-600">
                Egy hagyományos hirdetés gyorsan eltűnik, míg a FogPortálon 
                stabil, folyamatos jelenlétet biztosítunk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Mit kap, ha csatlakozik?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Teljes értékű online megjelenés professzionális eszközökkel és támogatással.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Prémium csomaggal saját aloldal</h3>
                  <p className="text-gray-600 text-lg">
                    Teljes értékű weboldal a FogPortálon belül egyedi design-nal, menükkel, 
                    kapcsolati adatokkal és szolgáltatásokkal. Minden rendelő egyedi URL-t kap 
                    (fogportal.hu/az-on-rendeloje).
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Országos jelenlét</h3>
                  <p className="text-gray-600 text-lg">
                    A páciensek könnyen rátalálnak, akár város, akár szolgáltatás szerint keresnek. 
                    Legyen jelen az ország minden pontján, ahol páciensek keresnek fogorvost.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Kiemelt megjelenés lehetősége</h3>
                  <p className="text-gray-600 text-lg">
                    Maximum 6 rendelő számára elérhető extra láthatóság a főoldal legjobb pozíciójában. 
                    Szabad helyek függvényében biztosítjuk a kiemelt megjelenést.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">PR cikk lehetőség</h3>
                  <p className="text-gray-600 text-lg">
                    Szakmai bemutatkozás lehetősége a FogPortál blogban. Mutassa be rendelőjét, 
                    szolgáltatásait és szakértelmét széles közönségnek.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:pl-12">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h1M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Példa Fogászat</h3>
                    <p className="text-blue-600 font-semibold">Budapest, V. kerület</p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Szolgáltatások:</span>
                      <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">12</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Havi látogatók:</span>
                      <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">2,450+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Érdeklődések:</span>
                      <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">180+</span>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500 text-center italic">
                      "A FogPortálon keresztül havonta 50+ új pácienst érünk el!"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visitor Statistics Section */}
      {(publicSettings.visitor_stats_image || publicSettings.monthly_visitors) && (
      <section className="py-20 bg-gradient-to-br from-blue-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-green-600 rounded-full flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Nézze meg, hányan keresnek fogorvost nálunk egy hónapban!
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              A FogPortál.hu kiemelkedő látogatottsággal rendelkezik, így Ön is biztos lehet benne, hogy rendelője ott lesz, ahol a páciensek keresnek. 
              Ennyi páciens kereste a következő fogorvosát nálunk.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
              {/* Full layout when both image and number are available */}
              {publicSettings.visitor_stats_image && publicSettings.monthly_visitors ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                
                {/* Statistics Image */}
                <div className="order-2 lg:order-1">
                  <div className="relative">
                    <img 
                      src={`${backendUrl}${publicSettings.visitor_stats_image}`} 
                      alt="Látogatottság statisztika" 
                      className="w-full rounded-xl shadow-xl border-4 border-gray-100"
                    />
                    <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-blue-600 to-green-600 text-white p-4 rounded-lg shadow-lg">
                      <div className="flex items-center space-x-2">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-semibold">Látogatási statisztika</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Statistics Content */}
                <div className="order-1 lg:order-2 text-center lg:text-left">
                  <div className="mb-8">
                    <div className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
                      {publicSettings.monthly_visitors.toLocaleString('hu-HU')}
                    </div>
                    <div className="text-2xl font-semibold text-gray-700 mb-2">Havi látogató</div>
                    <div className="text-lg text-gray-600">Megbízható, stabil és folyamatosan növekvő platform</div>
                  </div>

                  <div className="space-y-4 mb-8">
                    <div className="flex items-center justify-center lg:justify-start">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">Valós, hiteles látogatási adatok</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.793a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">Aktív, fogorvosokat kereső felhasználók</span>
                    </div>
                    <div className="flex items-center justify-center lg:justify-start">
                      <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-700">Országos lefedettség minden megyében</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl">
                    <p className="text-lg text-gray-700 font-medium">
                      👉 "Ez nem ígéret – ez a valóság. Íme, a havi látogatói statisztikánk, közvetlenül a rendszerből."
                    </p>
                  </div>
                </div>
              </div>
              ) : (
              /* Partial layout when only one field is available */
              <div className="text-center">
                {publicSettings.monthly_visitors && (
                  <div className="mb-8">
                    <div className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-4">
                      {publicSettings.monthly_visitors.toLocaleString('hu-HU')}
                    </div>
                    <div className="text-2xl font-semibold text-gray-700 mb-2">Havi látogató</div>
                    <div className="text-lg text-gray-600">Megbízható, stabil és folyamatosan növekvő platform</div>
                  </div>
                )}
                
                {publicSettings.visitor_stats_image && (
                  <div className="mb-8">
                    <img 
                      src={`${backendUrl}${publicSettings.visitor_stats_image}`} 
                      alt="Látogatottság statisztika" 
                      className="max-w-2xl mx-auto rounded-xl shadow-xl border-4 border-gray-100"
                    />
                  </div>
                )}
                
                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-xl max-w-2xl mx-auto">
                  <p className="text-lg text-gray-700 font-medium">
                    👉 "Ez nem ígéret – ez a valóság. {publicSettings.monthly_visitors ? 'Íme, a havi látogatói statisztikánk' : 'Íme, a látogatói statisztikánk'}, közvetlenül a rendszerből."
                  </p>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Egyszerű és átlátható díjszabás
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Válassza ki az Önnek legmegfelelőbb csomagot és kezdje el még ma!
            </p>
          </div>

          {/* Fő csomagok */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">Csomagjaink</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              
              {/* Alap csomag */}
              <div className="bg-white border-2 border-blue-200 rounded-2xl shadow-lg p-8 relative">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Alap csomag</h4>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">169.000</span>
                    <span className="text-lg text-gray-600"> Ft + ÁFA</span>
                    <div className="text-gray-500">/év</div>
                  </div>
                  <ul className="space-y-3 mb-8 text-left text-gray-600">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      SEO-optimalizált tartalom a jobb Google találatokért
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Listázás a portál keresőjében
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Országos szakmai jelenlét
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Célzott pácienseket ér el
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Költséghatékony, éves fix díjas megjelenés
                    </li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-bold hover:bg-blue-700 transition-colors"
                  >
                    Alap csomag választása
                  </button>
                </div>
              </div>

              {/* Prémium csomag */}
              <div className="bg-gradient-to-br from-blue-600 to-green-600 rounded-2xl shadow-2xl p-8 text-white relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold">
                    NÉPSZERŰ
                  </span>
                </div>
                <div className="text-center">
                  <h4 className="text-2xl font-bold mb-4">Prémium csomag</h4>
                  <div className="mb-6">
                    <div className="text-lg mb-1">169.000 Ft + ÁFA/év</div>
                    <div className="text-lg mb-2">+</div>
                    <div className="text-lg font-bold">189.000 Ft + ÁFA Egyszeri díj</div>
                  </div>
                  <ul className="space-y-3 mb-8 text-left">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Az alap csomag minden szolgáltatása
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Teljes értékű aloldal egyedi design-nal, amely teljes értékű modern, 
                      a mai online trendeket maximálisan kielégítő weboldalként funkcionál
                    </li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className="w-full bg-white text-blue-600 py-3 px-6 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                  >
                    Prémium csomag választása
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Kiegészítő szolgáltatások */}
          <div>
            <h3 className="text-3xl font-bold text-center text-gray-900 mb-8">Kiegészítő szolgáltatások</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
              
              {/* Kiemelt megjelenés */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">Kiemelt megjelenés a főoldalon</h4>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">77.000</span>
                    <span className="text-lg text-gray-600"> Ft + ÁFA</span>
                    <div className="text-gray-500">/hó</div>
                  </div>
                  <ul className="space-y-3 mb-8 text-left text-gray-600">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Maximum 6 rendelő számára
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Szabad helyek függvényében
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Extra láthatóság a főoldalon
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Kiemelt pozíció a keresésben
                    </li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-bold hover:bg-gray-800 transition-colors"
                  >
                    Érdekel a kiemelés
                  </button>
                </div>
              </div>

              {/* PR cikk */}
              <div className="bg-white border-2 border-gray-200 rounded-2xl shadow-lg p-8">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">PR cikk megjelenés</h4>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">45.000</span>
                    <span className="text-lg text-gray-600"> Ft + ÁFA</span>
                    <div className="text-gray-500">/cikk</div>
                  </div>
                  <ul className="space-y-3 mb-8 text-left text-gray-600">
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Szakmai bemutatkozás a blogban
                    </li>
                    <li className="flex items-center">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      SEO optimalizált tartalom
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 mr-3 text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <span>A cikk megírását nem tartalmazza</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 mr-3 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Igény esetén külön megbeszélés alapján biztosítjuk</span>
                    </li>
                  </ul>
                  <button 
                    onClick={scrollToContact}
                    className="w-full bg-gray-900 text-white py-3 px-6 rounded-lg font-bold hover:bg-gray-800 transition-colors"
                  >
                    Érdekel a PR cikk
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg">
              Egyedi igények esetén vegye fel velünk a kapcsolatot személyre szabott ajánlatért!
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Dynamic Content */}
      {testimonials.length > 0 && (
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Mit mondanak partnereink?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Csatlakozzon az elégedett fogászatok növekvő közösségéhez!
            </p>
          </div>

          {testimonialsLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Vélemények betöltése...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={testimonial.id} className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg key={i} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    {/* Show empty stars for remaining */}
                    {[...Array(5 - testimonial.rating)].map((_, i) => (
                      <svg key={`empty-${i}`} className="w-5 h-5 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center">
                    {testimonial.image ? (
                      <img 
                        src={`${backendUrl}${testimonial.image}`} 
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-gray-200"
                      />
                    ) : (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                        index % 3 === 0 ? 'bg-blue-600' : 
                        index % 3 === 1 ? 'bg-green-600' : 'bg-purple-600'
                      }`}>
                        <span className="text-white font-bold text-lg">
                          {testimonial.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-gray-600 text-sm">{testimonial.clinic_name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-green-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Legyen jelen a FogPortálon és érjen el több pácienst!
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto">
            Csatlakozzon Magyarország vezető fogászati portáljához és növelje rendelője látogatottságát 
            már a következő hónaptól. Kizárólagos lehetőség profi fogászatok számára.
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-blue-600 px-12 py-4 rounded-lg hover:bg-gray-100 transition-colors font-bold text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            Vegye fel velünk a kapcsolatot most!
          </button>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Vegye fel velünk a kapcsolatot!
            </h2>
            <p className="text-xl text-gray-600">
              Töltse ki az alábbi űrlapot, és munkatársunk 24 órán belül felveszi Önnel a kapcsolatot.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
            <form onSubmit={handleContactSubmit} className="space-y-6">
              {/* Honeypot field - hidden from users, but bots may fill it */}
              <input 
                type="text" 
                name="website_url" 
                value={contactForm.honeypot}
                onChange={(e) => setContactForm(prev => ({...prev, honeypot: e.target.value}))}
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Név *</label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => setContactForm(prev => ({...prev, name: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">E-mail cím *</label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => setContactForm(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Telefonszám *</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm(prev => ({...prev, phone: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Fogászat/Klinika neve *</label>
                  <input
                    type="text"
                    value={contactForm.clinicName}
                    onChange={(e) => setContactForm(prev => ({...prev, clinicName: e.target.value}))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    required
                  />
                </div>
              </div>

              {/* New Website field */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Weboldal</label>
                <input
                  type="text"
                  value={contactForm.website}
                  onChange={(e) => setContactForm(prev => ({...prev, website: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="www.pelda-fogaszat.hu vagy https://www.pelda-fogaszat.hu"
                />
              </div>

              {/* Service Interest Checkboxes */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-4">Melyik szolgáltatásunk érdekli?</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    'Alap csomag',
                    'Prémium csomag', 
                    'Kiemelt megjelenés',
                    'PR cikk megjelenés'
                  ].map(service => (
                    <label key={service} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={contactForm.services.includes(service)}
                        onChange={(e) => handleServiceChange(service, e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-sm text-gray-700">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Üzenet</label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm(prev => ({...prev, message: e.target.value}))}
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Írja ide üzenetét. Megrendelés esetén a számlázási adatokat!"
                />
              </div>

              {/* Privacy Policy Acceptance - GDPR */}
              <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <input
                  type="checkbox"
                  id="privacy-accept"
                  checked={contactForm.privacyAccepted}
                  onChange={(e) => setContactForm(prev => ({...prev, privacyAccepted: e.target.checked}))}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                  required
                />
                <label htmlFor="privacy-accept" className="text-sm text-gray-700">
                  Az üzenet elküldésével elfogadom az{' '}
                  <button
                    type="button"
                    onClick={navigateToPrivacy}
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    adatkezelési tájékoztatóban
                  </button>
                  {' '}foglaltakat, és hozzájárulok adataim kezeléséhez. *
                </label>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-green-600 text-white px-12 py-4 rounded-lg hover:from-blue-700 hover:to-green-700 transition-all duration-200 font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  Üzenet küldése
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                <p>
                  A megküldött adatokat bizalmasan kezeljük. 
                  <button onClick={navigateToPrivacy} className="text-blue-600 hover:underline ml-1">Adatkezelési tájékoztató</button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold text-blue-400 mb-4">FogPortál.hu</h3>
              <p className="text-gray-300 mb-6 max-w-md">
                Magyarország prémium fogászati portálja. Professzionális megoldások 
                fogászatok és páciensek összekapcsolására.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors">
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Szolgáltatások</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-300 hover:text-blue-400 transition-colors">Rendelő listázás</a></li>
                <li><a href="#" className="text-gray-300 hover:text-blue-400 transition-colors">Kiemelt megjelenés</a></li>
                <li><a href="#" className="text-gray-300 hover:text-blue-400 transition-colors">PR cikkek</a></li>
                <li><a href="#" className="text-gray-300 hover:text-blue-400 transition-colors">SEO optimalizálás</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Kapcsolat</h4>
              <ul className="space-y-2">
                <li><button onClick={navigateToPrivacy} className="text-gray-300 hover:text-blue-400 transition-colors">Adatkezelési tájékoztató</button></li>
                <li><button onClick={navigateToTerms} className="text-gray-300 hover:text-blue-400 transition-colors">ÁSZF</button></li>
                <li><a href="/megjelenes-fogaszatoknak" className="text-gray-300 hover:text-blue-400 transition-colors">Kapcsolat</a></li>
                <li><button onClick={navigateToHome} className="text-gray-300 hover:text-blue-400 transition-colors">Főoldal</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                © 2025 FogPortál.hu. Minden jog fenntartva.
              </p>
              <p className="text-gray-400 text-sm mt-4 md:mt-0">
                Professzionális fogászati portál megoldások
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Sitemap Component

export default B2BLanding;
